import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import {
    DownloadSimple,
    Lock,
    VideoCamera,
    CircleNotch,
    Package,
    ChartLine,
    ArrowLeft,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { downloadPNG, downloadSVG } from "@/lib/download";
import { api, endpoints } from "@/lib/api";
import { REEL_FORMATS, getFormat } from "@/projects/social-media-kit/formats";
import { fetchSponsors, fetchCurrentSponsor } from "@/projects/social-media-kit/sponsors";
import { animateReel, countUpCommunity } from "@/projects/social-media-kit/animateReel";

const STORAGE_KEY = "eviq_admin_token";

const AdminGate = ({ onAuthorized }) => {
    const [token, setToken] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!token) return;
        setBusy(true);
        // Verify by attempting a benign admin call (clear override with the same value)
        try {
            const r = await api.post(endpoints.setSponsorCurrent(), {
                admin_token: token,
                sponsor_name: null,
            });
            if (r.status >= 200 && r.status < 300) {
                window.localStorage.setItem(STORAGE_KEY, token);
                onAuthorized(token);
            }
        } catch (err) {
            toast.error("Invalid admin token");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
            <form
                onSubmit={submit}
                data-testid="admin-gate"
                className="w-full max-w-md rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-8"
            >
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#27272a] bg-black">
                        <Lock size={16} color="#a3e635" weight="fill" />
                    </div>
                    <div>
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            EVIQ · Social Kit
                        </span>
                        <h1 className="font-editorial text-2xl text-white">Team access</h1>
                    </div>
                </div>
                <p className="mt-6 text-sm text-zinc-500">
                    Enter the admin token to preview and download the monthly community reel
                    ahead of your posting schedule.
                </p>
                <input
                    type="password"
                    data-testid="admin-token-input"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Admin token"
                    className="mt-6 w-full rounded-md border border-[#27272a] bg-black px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none"
                />
                <button
                    type="submit"
                    data-testid="admin-token-submit"
                    disabled={busy || !token}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#a3e635] px-4 py-2.5 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                >
                    {busy ? <CircleNotch size={14} className="animate-spin" /> : null}
                    Unlock
                </button>
                <Link
                    to="/"
                    className="mt-4 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-200"
                >
                    <ArrowLeft size={12} /> Back to dashboard
                </Link>
            </form>
        </div>
    );
};

const SocialKitAdmin = () => {
    const [token, setToken] = useState(
        typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) || "" : ""
    );
    const [community, setCommunity] = useState(null);
    const [sponsors, setSponsors] = useState([]);
    const [currentSponsor, setCurrentSponsor] = useState(null);
    const [selectedFormatId, setSelectedFormatId] = useState("monthly-recap");
    const [counts, setCounts] = useState({ total: 0, by_format: [] });
    const [animating, setAnimating] = useState(false);
    const [animateProgress, setAnimateProgress] = useState(0);
    const [partnerQuery, setPartnerQuery] = useState("");
    const [ssoStatus, setSsoStatus] = useState(null);
    const [leads, setLeads] = useState({ total: 0, counts_by_tier: {}, leads: [] });
    const previewRef = useRef(null);

    useEffect(() => {
        if (!token) return;
        let ignore = false;
        (async () => {
            const [comm, list, sp, cts, sso, ldz] = await Promise.all([
                api.get(endpoints.community()).then((r) => r.data).catch(() => null),
                fetchSponsors(),
                fetchCurrentSponsor(),
                api.get(endpoints.downloads()).then((r) => r.data).catch(() => ({ total: 0, by_format: [] })),
                api.get(endpoints.ssoStatus()).then((r) => r.data).catch(() => null),
                api.get(endpoints.sponsorLeadsList(token)).then((r) => r.data).catch(() => ({ total: 0, counts_by_tier: {}, leads: [] })),
            ]);
            if (ignore) return;
            setCommunity(comm);
            setSponsors(list);
            setCurrentSponsor(sp);
            setCounts(cts);
            setSsoStatus(sso);
            setLeads(ldz);
        })();
        return () => {
            ignore = true;
        };
    }, [token]);

    const format = useMemo(() => getFormat(selectedFormatId) || REEL_FORMATS[0], [selectedFormatId]);

    const svgHtml = useMemo(() => {
        if (!community && format.kind === "community") return "";
        try {
            if (format.kind === "community") {
                return format.builder({
                    community,
                    color: "#a3e635",
                    sponsor: currentSponsor,
                });
            }
            // Member formats need a mock user — admin preview only uses community reel.
            return "";
        } catch {
            return "";
        }
    }, [format, community, currentSponsor]);

    if (!token) return <AdminGate onAuthorized={setToken} />;

    const getSvg = () => previewRef.current?.querySelector("svg");

    const handlePickSponsor = async (name) => {
        try {
            await api.post(endpoints.setSponsorCurrent(), {
                admin_token: token,
                sponsor_name: name,
            });
            const sp = await fetchCurrentSponsor();
            setCurrentSponsor(sp);
            toast.success(name ? `Sponsor set to ${name}` : "Cleared to monthly rotation");
        } catch {
            toast.error("Could not update sponsor");
        }
    };

    const handleDownloadPNG = async () => {
        await downloadPNG(getSvg(), `eviq-${format.id}-admin.png`, {
            width: format.width,
            height: format.height,
            scale: 2,
        });
        toast.success("PNG saved");
    };

    const handleDownloadSVG = () => {
        downloadSVG(getSvg(), `eviq-${format.id}-admin.svg`);
        toast.success("SVG saved");
    };

    const handleAnimate = async () => {
        if (animating) return;
        setAnimating(true);
        setAnimateProgress(0);
        try {
            const blob = await animateReel({
                builder: (frameProps) =>
                    format.builder({
                        community: frameProps.community || community,
                        color: "#a3e635",
                        sponsor: currentSponsor,
                    }),
                width: format.width,
                height: format.height,
                duration_ms: 3500,
                fps: 30,
                animate: countUpCommunity(community),
                scale: 1,
                onProgress: setAnimateProgress,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `eviq-${format.id}-admin.webm`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 500);
            toast.success("Animated reel saved");
        } catch (e) {
            toast.error("Animated reel failed", { description: e?.message || "" });
        } finally {
            setAnimating(false);
            setAnimateProgress(0);
        }
    };

    const communityFormats = REEL_FORMATS.filter((f) => f.kind === "community");
    const memberFormats = REEL_FORMATS.filter((f) => f.kind === "member");

    return (
        <div data-testid="social-kit-admin" className="min-h-screen bg-[#050505] text-white">
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: { background: "#0a0a0a", border: "1px solid #27272a", color: "#fff" },
                }}
            />
            <header className="border-b border-[#1f1f22] bg-black/70 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#27272a] bg-[#0a0a0a] text-zinc-400 hover:text-white"
                            data-testid="admin-back"
                        >
                            <ArrowLeft size={14} />
                        </Link>
                        <div>
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                EVIQ · Admin
                            </span>
                            <h1 className="font-editorial text-lg text-white">Social Kit</h1>
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="admin-signout"
                        onClick={() => {
                            window.localStorage.removeItem(STORAGE_KEY);
                            setToken("");
                        }}
                        className="text-xs text-zinc-500 hover:text-zinc-200"
                    >
                        Sign out
                    </button>
                </div>
            </header>

            <main className="mx-auto grid max-w-[1440px] grid-cols-1 gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_360px] lg:gap-8 lg:px-10 lg:py-12">
                {/* Preview */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8">
                    <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                        <div>
                            <span className="eviq-chip">Monthly Community Reel · Preview</span>
                            <h2 className="mt-3 font-editorial text-2xl leading-tight text-white sm:text-3xl">
                                Ahead of your posting schedule.
                            </h2>
                            <p className="mt-2 max-w-lg text-sm text-zinc-500">
                                Preview and download the community reel your team will publish.
                                Sponsor picker below overrides the auto-rotation.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {communityFormats.map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    data-testid={`admin-format-${f.id}`}
                                    onClick={() => setSelectedFormatId(f.id)}
                                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ${
                                        f.id === selectedFormatId
                                            ? "border-[#a3e635] bg-[#0a0f05] text-[#a3e635]"
                                            : "border-[#27272a] bg-[#050505] text-zinc-300 hover:border-[#3a3a3f]"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mx-auto w-full max-w-[360px]">
                        <div
                            ref={previewRef}
                            data-testid="admin-preview"
                            className={`w-full overflow-hidden rounded-md border border-[#1f1f22] bg-black ${format.aspectRatioClass} [&>svg]:block [&>svg]:h-full [&>svg]:w-full`}
                            dangerouslySetInnerHTML={{ __html: svgHtml }}
                        />
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                        <button
                            type="button"
                            data-testid="admin-download-png"
                            onClick={handleDownloadPNG}
                            className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-4 py-2 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                        >
                            <DownloadSimple size={14} weight="bold" />
                            PNG · {format.width * 2}×{format.height * 2}
                        </button>
                        <button
                            type="button"
                            data-testid="admin-download-svg"
                            onClick={handleDownloadSVG}
                            className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2 text-sm text-zinc-200 hover:border-[#3a3a3f]"
                        >
                            SVG
                        </button>
                        <button
                            type="button"
                            data-testid="admin-download-animated"
                            onClick={handleAnimate}
                            disabled={animating}
                            className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2 text-sm text-zinc-200 hover:border-[#3a3a3f] disabled:opacity-60"
                        >
                            {animating ? (
                                <>
                                    <CircleNotch size={14} className="animate-spin" />
                                    {Math.round(animateProgress * 100)}%
                                </>
                            ) : (
                                <>
                                    <VideoCamera size={14} weight="fill" />
                                    Animated WebM
                                </>
                            )}
                        </button>
                    </div>
                </section>

                {/* Sidebar */}
                <aside className="flex flex-col gap-6">
                    {/* Sponsor picker */}
                    <div className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-5">
                        <div className="mb-4 flex items-center gap-2 text-zinc-500">
                            <Package size={14} color="#a3e635" weight="fill" />
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                                Sponsor · This month
                            </span>
                        </div>
                        <p className="mb-4 text-sm text-zinc-300">
                            {currentSponsor
                                ? `Currently: ${currentSponsor.name}${
                                      currentSponsor.source === "admin_override" ? " (override)" : ""
                                  }`
                                : "No sponsor set"}
                        </p>
                        <div className="flex flex-col gap-1">
                            <button
                                type="button"
                                data-testid="admin-sponsor-clear"
                                onClick={() => handlePickSponsor(null)}
                                className="rounded-md border border-[#1f1f22] bg-[#050505] px-3 py-2 text-left text-xs text-zinc-400 hover:border-[#3a3a3f]"
                            >
                                Auto (monthly rotation)
                            </button>
                            {sponsors.map((s) => (
                                <button
                                    key={s.name}
                                    type="button"
                                    data-testid={`admin-sponsor-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                                    onClick={() => handlePickSponsor(s.name)}
                                    className={`rounded-md border px-3 py-2 text-left text-xs transition-colors duration-200 ${
                                        currentSponsor?.name === s.name
                                            ? "border-[#a3e635] bg-[#0a0f05] text-[#a3e635]"
                                            : "border-[#1f1f22] bg-[#050505] text-zinc-300 hover:border-[#3a3a3f]"
                                    }`}
                                >
                                    {s.name}
                                    <div className="text-[10px] text-zinc-500">{s.underwriting}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Analytics */}
                    <div
                        data-testid="admin-analytics"
                        className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-5"
                    >
                        <div className="mb-4 flex items-center gap-2 text-zinc-500">
                            <ChartLine size={14} color="#a3e635" weight="fill" />
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                                Download analytics · All time
                            </span>
                        </div>
                        <div className="mb-4">
                            <div className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                Total downloads
                            </div>
                            <div className="mt-1 font-mono-metric text-3xl font-bold text-white">
                                {counts.total || 0}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            {REEL_FORMATS.map((f) => {
                                const rec = counts.by_format.find((d) => d.format_id === f.id);
                                const c = rec?.count || 0;
                                const pct = counts.total ? Math.round((c / counts.total) * 100) : 0;
                                return (
                                    <div key={f.id} className="rounded-md border border-[#1f1f22] bg-[#050505] p-3">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="truncate pr-2 text-zinc-200">{f.label}</span>
                                            <span className="font-mono-metric text-white">{c}</span>
                                        </div>
                                        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-[#0a0a0a]">
                                            <div
                                                className="h-full rounded-full bg-[#a3e635]"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="mt-4 text-[10px] text-zinc-500">
                            The dashboard grid auto-reorders formats by download count (most
                            popular first).
                        </p>
                    </div>

                    <div className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <span
                                className="inline-block h-1.5 w-1.5 rounded-full"
                                style={{ background: ssoStatus?.configured ? "#a3e635" : "#f59e0b" }}
                            />
                            <span
                                className="font-mono-metric text-[10px] uppercase tracking-[0.22em]"
                                style={{ color: ssoStatus?.configured ? "#a3e635" : "#f59e0b" }}
                            >
                                SSO · {ssoStatus?.configured ? "Ready" : "Not configured"}
                            </span>
                        </div>
                        <h3 className="font-editorial text-lg text-white">
                            eviq.pro handshake
                        </h3>
                        <p className="mt-2 text-xs text-zinc-400">
                            {ssoStatus?.configured
                                ? `EVIQ_SSO_SECRET is set. Issuer: ${ssoStatus.issuer}. Ready to accept HS256 tokens.`
                                : "Set EVIQ_SSO_SECRET in the backend env and coordinate with the eviq.pro dev using the playbook."}
                        </p>
                        <a
                            href="/docs/eviq-sso.md"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1 text-xs text-[#a3e635] hover:text-[#c8f37f]"
                        >
                            Open SSO playbook →
                        </a>
                    </div>

                    <div
                        data-testid="admin-sponsor-leads"
                        className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-5"
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                ESG partner leads
                            </span>
                            <span className="font-mono-metric text-[10px] text-zinc-600">
                                {leads.total}
                            </span>
                        </div>
                        {leads.leads.length === 0 && (
                            <p className="text-xs text-zinc-500">
                                No inquiries yet. Tier CTAs on /partners will land here.
                            </p>
                        )}
                        {leads.leads.length > 0 && (
                            <div className="flex flex-col gap-2">
                                {leads.leads.slice(0, 8).map((l) => (
                                    <div
                                        key={l.id}
                                        data-testid={`admin-lead-${l.id}`}
                                        className="rounded-md border border-[#1f1f22] bg-black/40 px-3 py-2"
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="truncate text-xs text-zinc-200">
                                                {l.company}
                                            </span>
                                            <span
                                                className="font-mono-metric text-[9px] uppercase tracking-[0.2em]"
                                                style={{ color: "#a3e635" }}
                                            >
                                                {l.tier_id}
                                            </span>
                                        </div>
                                        <div className="mt-1 truncate text-[11px] text-zinc-500">
                                            {l.contact_name} · {l.email}
                                        </div>
                                    </div>
                                ))}
                                {leads.leads.length > 8 && (
                                    <p className="text-[10px] text-zinc-600">
                                        + {leads.leads.length - 8} more…
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="rounded-lg border border-[#a3e635]/30 bg-gradient-to-br from-[#0a0f05] via-[#080808] to-[#050505] p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a3e635]" />
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-[#a3e635]">
                                Partner keepsakes
                            </span>
                        </div>
                        <h3 className="font-editorial text-lg text-white">
                            Certificate + Attribution
                        </h3>
                        <p className="mt-2 text-xs text-zinc-400">
                            Generate a personalized Fortune-500-style certificate for any
                            sponsor, or view their private monthly attribution report.
                        </p>
                        <input
                            type="search"
                            data-testid="admin-partner-search"
                            value={partnerQuery}
                            onChange={(e) => setPartnerQuery(e.target.value)}
                            placeholder={`Search ${sponsors.length} sponsors…`}
                            className="mt-4 w-full rounded-md border border-[#27272a] bg-black px-3 py-2 text-xs text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none"
                        />
                        <div className="mt-3 flex max-h-[360px] flex-col gap-2 overflow-y-auto pr-1">
                            {sponsors
                                .filter((s) =>
                                    s.name.toLowerCase().includes(partnerQuery.toLowerCase().trim()) ||
                                    (s.underwriting || "")
                                        .toLowerCase()
                                        .includes(partnerQuery.toLowerCase().trim())
                                )
                                .map((s) => {
                                const slug = s.name
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, "-")
                                    .replace(/^-|-$/g, "");
                                return (
                                    <div
                                        key={s.name}
                                        data-testid={`admin-partner-row-${slug}`}
                                        className="flex items-center justify-between gap-2 rounded-md border border-[#1f1f22] bg-black/40 px-3 py-2"
                                    >
                                        <span className="truncate text-xs text-zinc-200">
                                            {s.name}
                                        </span>
                                        <div className="flex gap-1.5">
                                            <Link
                                                to={`/certificates/${slug}`}
                                                data-testid={`admin-partner-cert-${slug}`}
                                                className="rounded-full bg-[#a3e635] px-2.5 py-1 text-[10px] font-semibold text-black hover:-translate-y-0.5 transition-transform"
                                            >
                                                Certificate
                                            </Link>
                                            <Link
                                                to={`/partners/${slug}`}
                                                data-testid={`admin-partner-attribution-${slug}`}
                                                className="rounded-full border border-[#27272a] bg-[#050505] px-2.5 py-1 text-[10px] text-zinc-300 hover:border-[#3a3a3f]"
                                            >
                                                Report
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                            {sponsors.filter((s) =>
                                s.name.toLowerCase().includes(partnerQuery.toLowerCase().trim())
                            ).length === 0 && partnerQuery && (
                                <p className="py-4 text-center text-xs text-zinc-500">
                                    No sponsors match “{partnerQuery}”.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-5">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                            Member formats
                        </span>
                        <p className="mt-2 text-xs text-zinc-500">
                            The following are personal to each member and can only be previewed
                            on their dashboard:
                        </p>
                        <ul className="mt-3 flex flex-col gap-1 text-xs text-zinc-300">
                            {memberFormats.map((f) => (
                                <li key={f.id}>
                                    · {f.label}{" "}
                                    <span className="text-zinc-500">({f.size})</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </aside>
            </main>
        </div>
    );
};

export default SocialKitAdmin;
